import { Dropdown, FormInput } from "@Rolec-Services/rolec-ui";
import { useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { StepProps } from "~/components/organisms/step";
import { countries } from "~/schemas/countries";
import {
  addressSearchInitialState,
  addressSearchReducer,
} from "~/utils/address-search";
import {
  fetchAutocomplete,
  formatAutocompleteData,
} from "~/utils/autocomplete";
import { debounce } from "~/utils/debounce";
import { tryCatch } from "~/utils/try-catch";
import {
  fetchValidate,
  formatValidateData,
  updateAddressValues,
} from "~/utils/validate";
import { AddressFields } from "../address-fields";
import { SearchBar } from "../search-bar";

type SiteProps = {
  control: StepProps["control"];
  setValue: StepProps["setValue"];
};

export function Site({ control, setValue }: SiteProps) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(
    addressSearchReducer,
    addressSearchInitialState
  );

  const debouncedSearchInput = debounce((value: string) => {
    dispatch({ type: "SET_SEARCH", payload: value });
  }, 500);

  async function validateSelectedResult(id: string, summaryline: string) {
    const result = await tryCatch(
      fetchValidate(id, summaryline, state.country)
    );

    if (result.error) {
      console.error("Error validating data:", result.error);
      return;
    }

    updateAddressValues(
      "site",
      formatValidateData(result.data, summaryline),
      state.country,
      setValue
    );

    dispatch({ type: "CLEAR_SEARCH" });
  }

  useEffect(() => {
    if (!state.search) {
      dispatch({ type: "SET_RESULTS", payload: [] });
      return;
    }

    async function fetchData() {
      const result = await tryCatch(
        fetchAutocomplete(state.search, state.country)
      );

      if (result.error) {
        dispatch({ type: "SET_RESULTS", payload: [] });
        return;
      }

      dispatch({
        type: "SET_RESULTS",
        payload: formatAutocompleteData(result.data),
      });
    }

    fetchData();
  }, [state.search, state.country]);

  return (
    <>
      <FormInput
        errorMessage={t("Required")}
        control={control}
        name="site.name"
        label={t("Site Name")}
      />
      <div className="flex w-full gap-2 justify-between items-center">
        <SearchBar
          value={state.input}
          onChange={(e) => {
            dispatch({ type: "SET_INPUT", payload: e.target.value });
            debouncedSearchInput(e.target.value);
          }}
          label={{ children: t("Search Address") }}
          searchResults={{
            searchResults: state.results,
            resultClick: validateSelectedResult,
          }}
        />
        <div className="pt-[18px]">
          <Dropdown
            value={state.country}
            onValueChange={(value) => {
              dispatch({ type: "SET_COUNTRY", payload: value });
            }}
            items={countries}
            placeholder="Country"
          />
        </div>
      </div>
      <AddressFields control={control} name="site" />
    </>
  );
}
